import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import cx from 'classnames';

import { withRouter } from 'core/libs/router';
import { compose } from 'core/libs/recompose';

import withBreakpoint from 'core/hocs/withBreakpoint';
import withTopic from 'core/hocs/withTopic';
import withTheme from 'core/hocs/withTheme';
import { callYaMetrikaMethod } from 'core/utils/counters-helper';

import loadScript from 'core/utils/load-script';

import HelmetStateChange from '../App/HelmetStateChange';

import skipRenderWithSharingDisabled from './skipRenderWithSharingDisabled';
import styles from './index.styl';

const SDK_URL = 'https://developers.rambler.ru/likes/v1/widget.js';

export const SHARE = [
  'vkontakte',
  'odnoklassniki',
  'telegram',
  'whatsapp',
  'livejournal',
  'copy',
];

export const SHARE_MOBILE = [
  'copy',
  'email',
  'telegram',
  'whatsapp',
  'odnoklassniki',
  'vkontakte',
];

/**
 * @author Artyom Galianov <a.galianov@rambler-co.ru>
 *
 * В основе компонента: https://developers.rambler.ru/likes/#/show/slide/1
 *
 * Все свойства переданные в компонент будут передаваться в метод
 * `RamblerShare.init('.class-name', { ...props })` в том же виде.
 */

class Socializator extends PureComponent {
  static propTypes = {
    /** Список кнопок шаринга для отображения компонентом */
    buttons: PropTypes.array,
    /** @ignore */
    isMobile: PropTypes.bool,
    /** utm-метка */
    utm: PropTypes.string,
    /** Объект настроек для кнопки шаринга vkontakte */
    vkontakte: PropTypes.object,
    /** Объект настроек для кнопки шаринга odnoklassniki */
    odnoklassniki: PropTypes.object,
    /** Объект настроек для кнопки шаринга livejournal */
    livejournal: PropTypes.object,
    /** Объект настроек для кнопки шаринга facebook */
    facebook: PropTypes.object,
    /** Объект настроек для кнопки шаринга twitter */
    twitter: PropTypes.object,
    /** Объект настроек для кнопки шаринга telegram */
    telegram: PropTypes.object,
    /** Объект настроек для кнопки шаринга whatsapp */
    whatsapp: PropTypes.object,
    /**
     * Обработчик события share
     */
    shareCallback: PropTypes.func,

    /** @ignore */
    topic: PropTypes.object,
    /**
     * если Socializator не на странице топика, то параметр url будет использоваться
     * при передаче аналогичного параметра в RamblerShare. Ссылка должна быть относительная
     */
    url: PropTypes.string,
    /** @ignore */
    theme: PropTypes.object,
    /** @ignore */
    history: PropTypes.object,
  };

  static contextTypes = {
    host: PropTypes.string,
    counterIds: PropTypes.object,
  };

  static defaultProps = {
    utm: 'utm_source=social',
  };

  state = {
    opened: !this.props.isMobile,
    isHidden: false,
  };

  componentDidMount() {
    loadScript(SDK_URL, 'RamblerShare').then(sdk => {
      this.sdk = sdk;
      this.initRamblerShare();
      if (this.props.topic) {
        this.unlisten = this.props.history.listen((location) => {
          this.setState({
            isHidden: location.pathname !== this.props.topic.content.attributes.link,
            opened: false,
          });
        });
      }
    });
  }

  componentWillUnmount() {
    if (this.sdk) this.sdk.destroy(this.DOMNode);
    this.unlisten && this.unlisten();
    this.unmounted = true;
  }

  initRamblerShare = () => {
    if (!this.unmounted && this.sdk && this.DOMNode) {
      this.sdk.destroy(this.DOMNode);
      const {
        isMobile,
        buttons: btns,
        topic,
        theme: {
          controls: {
            socializator,
          },
        },
        url,
      } = this.props;

      const buttons = btns || (isMobile ? SHARE_MOBILE : SHARE);
      // eslint-disable-next-line no-unused-vars
      const { iconsGap, ...restStyles } = socializator;

      const options = {
        ...this.props,
        buttons,
        facebook: {
          ...this.props.facebook,
          /**
           * Подмешиваем useRamblerIdServerRequest для обхода ограничения
           * fb по кол-ву запросов.
           * Подробнее тут: https://confluence.rambler-co.ru/pages/viewpage.action?pageId=36640586
           */
          useRamblerIdServerRequest: true,
        },
        style: restStyles,
        counters: false,
        ...(topic || url) && { url: topic ? `https://${this.context.host}${topic.content.attributes.link }` : url },
      };

      this.sdk.init(this.DOMNode, options);

      this.sdk.events.on('share', (socialNetwork) => {
        callYaMetrikaMethod(this.context.counterIds.yandexMetrika, 'reachGoal', 'block_social_on_schering_zag');
        if (this.props.shareCallback) {
          this.props.shareCallback(socialNetwork);
        }
      });
    }
  };

  toggle = () => {
    this.setState({ opened: !this.state.opened });
  };

  render() {
    const { opened, isHidden } = this.state;
    const {
      isMobile,
      theme: {
        controls: {
          socializator,
        },
      },
      topic,
      history: {
        location: {
          pathname,
        },
      },
    } = this.props;

    const {
      buttonHeight,
      borderRadius,
      color,
      iconsGap,
      fixedPosition,
    } = socializator;

    const canSticky = isMobile && fixedPosition;

    const {
      background,
      boxShadow,
      icon: Icon,
      iconSize,
    } = socializator[opened ? 'opened' : 'closed'] || {};

    const currentTopic = topic && pathname === topic.content.attributes.link;

    return (
      <div
        data-qa='socializator'
        className={cx(
          styles.socializator,
          canSticky && styles._fixed,
          canSticky && fixedPosition === 'left' && styles._left,
          isHidden && styles._hidden,
          opened && styles._opened,
          currentTopic && styles._currentTopic,
        )}
      >
        <style jsx>{`
          .${styles.ramblerShare}
            gap ${iconsGap}px

            .${styles._fixed} &
              width ${buttonHeight}px

          .${styles.more}
            border-radius ${borderRadius}px
            width ${buttonHeight}px
            height ${buttonHeight}px
            background ${background}
            box-shadow ${boxShadow}
            margin-top ${iconsGap}px
            svg
              color ${color}
              width ${iconSize}px
              height ${iconSize}px
        `}</style>
        <HelmetStateChange onChange={this.initRamblerShare}>
          <div
            className={cx(
              'rambler-share',
              styles.ramblerShare,
            )}
            ref={node => (this.DOMNode = node)}
          />
          {canSticky && (
            <div onClick={this.toggle} className={styles.more}>
              <Icon />
            </div>
          )}
        </HelmetStateChange>
      </div>
    );
  }
}

const SocializatorWithHOCs = compose(
  skipRenderWithSharingDisabled,
  withTopic,
  withBreakpoint,
  withTheme,
  withRouter,
)(Socializator);
SocializatorWithHOCs.displayName = 'Socializator';

export { Socializator as StorybookComponent };
export default SocializatorWithHOCs;
